"use client";

import Link from "next/link";
import { startTransition, useActionState, type CSSProperties } from "react";
import { requestInvite, type InviteRequestResult } from "@/app/actions";
import { brandCssVars, type SiteBranding } from "@/lib/branding";
import { brandFontClass, brandFontVariableClass } from "@/lib/brand-fonts";
import { BrandLogo } from "@/components/public/brand-logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export function HomeLanding({ branding }: { branding: SiteBranding }) {
  const brandStyle = brandCssVars(branding.theme) as CSSProperties;
  const fontClass = brandFontClass(branding.theme.fontFamily);
  const [state, formAction, pending] = useActionState(requestInvite, null);

  return (
    <div
      className={`${brandFontVariableClass} ${fontClass} relative flex min-h-screen flex-col text-neutral-900`}
      style={brandStyle}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div className="absolute -top-24 right-[-10%] h-72 w-72 rounded-full bg-neutral-900/[0.04] blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-8%] h-80 w-80 rounded-full bg-neutral-900/[0.03] blur-3xl" />
      </div>

      <header className="relative z-10 flex justify-end px-6 py-5 sm:px-10">
        <Button asChild variant="outline" size="sm">
          <Link href="/admin">Admin Login</Link>
        </Button>
      </header>

      <main className="relative z-10 mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-6 pb-12 pt-4 sm:px-10 sm:pb-20">
        <div className="animate-in fade-in slide-in-from-bottom-3 duration-700 fill-mode-both">
          {branding.logoUrl ? (
            <BrandLogo src={branding.logoUrl} priority />
          ) : (
            <p className="text-4xl font-semibold tracking-tight sm:text-5xl">
              XL Law
              <span className="mt-1 block text-2xl font-normal text-neutral-500 sm:text-3xl">
                &amp; Consulting
              </span>
            </p>
          )}
          <h1 className="mt-8 text-xl font-medium tracking-tight text-neutral-800 sm:text-2xl">
            Event invitations &amp; RSVP
          </h1>
          <p className="mt-3 max-w-md text-base leading-relaxed text-neutral-600">
            Attendees RSVP through a personal link in their invitation email. If you
            don&apos;t have one yet, request an invitation below.
          </p>
        </div>

        <div
          className="mt-10 animate-in fade-in slide-in-from-bottom-3 rounded-2xl border border-neutral-200/80 p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] duration-700 fill-mode-both sm:p-8 [animation-delay:120ms]"
          style={{ background: "var(--brand-rsvp-panel)" }}
        >
          <h2 className="text-lg font-semibold tracking-tight">
            Request an invitation
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            Tell us who you are and which event you&apos;d like to attend.
          </p>

          {state?.success ? (
            <SuccessMessage message={state.success} />
          ) : (
            <InviteRequestForm
              formAction={formAction}
              pending={pending}
              state={state}
            />
          )}
        </div>
      </main>

      <footer className="relative z-10 animate-in fade-in px-6 py-6 text-center text-xs text-neutral-400 duration-700 fill-mode-both sm:px-10 [animation-delay:220ms]">
        XL Law &amp; Consulting
      </footer>
    </div>
  );
}

function InviteRequestForm({
  formAction,
  pending,
  state,
}: {
  formAction: (payload: FormData) => void;
  pending: boolean;
  state: InviteRequestResult | null;
}) {
  return (
    <form
      className="mt-6 space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        startTransition(() => {
          formAction(new FormData(e.currentTarget));
        });
      }}
    >
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          name="name"
          autoComplete="name"
          required
          maxLength={200}
          className="h-10"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          maxLength={200}
          className="h-10"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="institution">Institution</Label>
        <Input
          id="institution"
          name="institution"
          autoComplete="organization"
          required
          maxLength={200}
          className="h-10"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="event">Event</Label>
        <Input
          id="event"
          name="event"
          required
          maxLength={200}
          placeholder="Name of the event"
          className="h-10"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="note">
          Note <span className="font-normal text-neutral-400">(optional)</span>
        </Label>
        <Textarea
          id="note"
          name="note"
          maxLength={2000}
          placeholder="Anything else we should know"
          className="min-h-24"
        />
      </div>
      {state?.error && (
        <p className="text-sm text-red-600" role="alert">
          {state.error}
        </p>
      )}
      <Button type="submit" className="w-full" size="lg" disabled={pending}>
        {pending ? "Sending..." : "Request invitation"}
      </Button>
    </form>
  );
}

function SuccessMessage({ message }: { message: string }) {
  return (
    <div className="mt-6 rounded-xl bg-neutral-50 px-4 py-6 text-center">
      <p className="text-sm leading-relaxed text-neutral-700">{message}</p>
    </div>
  );
}
